import { applySnapshot, flow, Instance, onAction, onSnapshot, types } from 'mobx-state-tree'
import { actionMenuStrings } from '../../../src/data/static/strings'
import { Routes } from '../../nav/routes'
import {
  fromClDateTime,
  fromDateTime,
  getSelectedDate,
  isOsMobile,
  tableConfig,
  toCLDateTime,
  toDateTime,
} from '../../util/util'
import { BeneficiaryOnboarding, BeneficiaryOnboardingData } from '../models/BeneficiaryOnboarding'
import { BillPayments, BillPaymentsData } from '../models/BillPayments'
import { BulkRefund, bulkRefundInitialData } from '../models/BulkRefund'
import { Campaigns, CampaignsData } from '../models/Campaigns'
import { Insights, InsightsData } from '../models/Insights'
import { Invoices, InvoicesData } from '../models/Invoices'
import { Links, LinksInitialData } from '../models/Links'
import { PayoutHistory, PayoutHistoryData } from '../models/PayoutHistory'
import { PayoutLinks, PayoutLinksData } from '../models/PayoutLinks'
import { configData, QrAgentConfig } from '../models/QrAgentConfig'
import { QrAgentSession, sessionDataInitial } from '../models/QrAgentSession'
import { QrCodeUser, userInitialDataAgent } from '../models/QrCodeUser'
import { Reports, ReportsData } from '../models/Reports'
import { Response } from '../models/Response'
import { Settings, settingsData, SettingsV2 } from '../models/Settings'
import { SettlementInitialData, Settlements } from '../models/Settlements'
import { SubscriptionHistory, SubscriptionHistoryData } from '../models/SubscriptionHistory'
import { Superset, SupersetData } from '../models/Superset'
import { Transactions, transactionsData } from '../models/Transactions'
import { TransactionTrendData, TransactionTrends } from '../models/TransactionTrends'
import { User, userInitialData, userInitialData1 } from '../models/User'
import { UserManagement, UserManagementData } from '../models/UserManagement'
import { TokenizationRequests, TokenizationRequestsData } from '../models/Vault'
import { VoiceBoxes, VoiceBoxesData } from '../models/VoiceBoxes'
import { sortBy, tableItemStrings } from '../static/strings'
import { INFINITE_SCROLL_WINDOW_INDEX } from './../../util/common'

const Root = types
  .model('Root', {
    user: User,
    users: types.array(User),
    transactions: Transactions,
    settlements: Settlements,
    transactionTrends: TransactionTrends,
    bulkRefund: BulkRefund,
    settings: Settings,
    invoices: Invoices,
    response: Response,
    links: Links,
    subscriptions: SubscriptionHistory,
    insights: Insights,
    userManagement: UserManagement,
    campaigns: Campaigns,
    tokenizationRequests: TokenizationRequests,
    reports: Reports,
    agentQrSession: types.optional(QrAgentSession, {}),
    agentQrUser: types.optional(QrCodeUser, {}),
    agentQrConfig: types.optional(QrAgentConfig, {}),
    voiceBoxes: VoiceBoxes,
    beneficiaryOnboarding: BeneficiaryOnboarding,
    payoutHistory: PayoutHistory,
    payoutLinks: PayoutLinks,
    billPayments: BillPayments,
    superset: Superset,
  })
  .volatile(() => ({}))
  .actions((self) => ({
    init(): void {},
    reset(): void {
      RootStore.user.session.updateAppSessionData('', '', -1, -1, '')
      RootStore.user.session.updateUserSessionData('', '', '', -1, -1, '')
    },
    getSelectedMerchant(): any {
      const data = self.user?.merchants?.find((item: any) => item?.mid === self?.user?.selectedMid)
      return data
    },
    updateSelectedMerchant(updatedMerchant: any): void {
      const oldMerchantsData = self.user.merchants
      if (oldMerchantsData?.length > 0) {
        let selectedMerchant = oldMerchantsData.find((item: any) => item?.mid === updatedMerchant?.mid)
        let newMerchants: Array<object> = []
        self.user.merchants.forEach((element: any) => {
          if (element.mid === (selectedMerchant?.mid ?? '')) {
            newMerchants.push({ ...element, ...updatedMerchant })
          } else {
            newMerchants.push(element)
          }
        })
        self.user.merchants = JSON.parse(JSON.stringify(newMerchants))
      } else {
        self.user.merchants.push(JSON.parse(JSON.stringify(updatedMerchant)))
      }
    },
    updateUserSettings(newSettings: SettingsV2): void {
      applySnapshot(self.settings, newSettings)
    },
    updateUsers(newUsers: any[]): void {
      applySnapshot(self.users, newUsers)
    },
    deleteUser(index: number): void {
      if (index <= self.users.length - 1) {
        self.users.splice(index, 1)
      }
    },
    updateContactEmail(emailId: string): void {
      const own = self.user.contact
      own.updateContactEmail(emailId)
    },
    updateContactWebsite(webaddress: string): void {
      const own = self.user.contact
      own.updateContactWebsite(webaddress)
    },

    setStepKey(key: string): void {
      self.settings.metaData.stepKey = key
    },
    extendUserAndAppSession: flow(function* extendUserAndAppSession() {
      const data = yield RootStore.user.session.appExtendSession()
      let userTokens
      if (data.status === 'SUCCESS') {
        userTokens = yield RootStore.user.session.userExtendSession()
      }
      return userTokens
    }),
    fetchRecentTransactionsAction(value: any, fetchTxn: any, fetchSettlements: any, fetchRefunds: any): any {
      const mid = self.user.selectedMid
      const toDate = self.settings.metaData.selectedDate.toDate
      const fromDate = self.settings.metaData.selectedDate.fromDate
      const dateLabel = self.settings.metaData.selectedDate.dateLabel
      const fetchTransactionObj = {
        0: () => {
          if (mid !== '' && value === 0) {
            fetchTxn(
              1,
              10,
              `${sortBy.sortField},${sortBy.sortByDesc}`,
              fromDateTime(fromDate),
              toDateTime(toDate ?? new Date(Date.now())),
              mid,
              'JSON',
              false,
              ...Array(9).fill(undefined),
              dateLabel === 'custom',
              self.settings.metaData.selectedDate,
              true,
            )
          }
        },
        1: () => {
          fetchSettlements(
            mid,
            fromDateTime(fromDate),
            toDateTime(toDate ?? new Date(Date.now())),
            1,
            10,
            false,
            dateLabel === 'custom',
          )
        },
        2: () => {
          fetchRefunds(
            1,
            10,
            tableItemStrings.transactionDesc,
            fromDateTime(fromDate),
            toDateTime(toDate ?? new Date(Date.now())),
            mid,
            ...Array(2).fill(undefined),
            dateLabel === 'custom',
            true,
          )
        },
      }
      fetchTransactionObj[value.toString()] && fetchTransactionObj[value.toString()]()
    },
    fetchTransactionDetailsOfPath(
      fetchTxnColumns: any,
      fetchTxn: any,
      isForTransactionTab = false,
      selectedDateInfo: any,
      pagination: any,
      restrictTransactionCount: boolean = false,
      isMobile?: any,
      abortController?: any,
    ) {
      let pathName = Routes.TRANSACTIONS
      const pageNumber = pagination.number === 0 ? 1 : pagination.number
      const size = pagination.size < 10 ? 10 : pagination.size
      const mid = self.user.selectedMid
      const isFieldsNotEmpty = self.transactions.isFieldsNotEmpty
      const toDate = selectedDateInfo?.toDate ?? self.settings.metaData.selectedDate.toDate
      const fromDate = selectedDateInfo?.fromDate ?? self.settings.metaData.selectedDate.fromDate
      const fetchDetailsObj = {
        [Routes.TRANSACTIONS]: () => {
          !isFieldsNotEmpty && mid && fetchTxnColumns()
          toDate &&
            mid &&
            fetchTxn(
              pageNumber,
              size,
              `${sortBy.sortField},${sortBy.sortByDesc}`, // FIXME: Phase 2- implement if different fields are available for sorts
              fromDateTime(fromDate),
              toDateTime(toDate ?? new Date(Date.now())),
              mid,
              ...Array(9).fill(undefined),
              isForTransactionTab ? 'Transactions' : undefined,
              restrictTransactionCount,
              undefined,
              selectedDateInfo,
              undefined,
              isMobile,
              abortController,
            )
        },
      }
      return fetchDetailsObj[pathName]()
    },
    fetchRefundDetailsOfPath(fetchRefunds: any, selectedDateInfo: any, ismobile?: any, abortController?: any) {
      const pagination = isOsMobile() ? self.transactions.paginationRefunded : self.transactions.pagination
      let pathName = Routes.REFUNDS
      const pageNumber = pagination.number === 0 ? 1 : pagination.number
      const size = pagination.size < 10 ? 10 : pagination.size
      const mid = self.user.selectedMid
      const toDate = selectedDateInfo?.toDate ?? self.settings.metaData.selectedDate.toDate
      const fromDate = selectedDateInfo?.fromDate ?? self.settings.metaData.selectedDate.fromDate
      const fetchDetailsObj = {
        [Routes.REFUNDS]: () => {
          mid &&
            fetchRefunds(
              pageNumber,
              size,
              undefined,
              fromDateTime(fromDate),
              toDateTime(toDate ?? new Date(Date.now())),
              mid,
              undefined,
              'Transactions',
              undefined,
              undefined,
              ismobile,
              abortController,
            )
        },
      }
      return fetchDetailsObj[pathName]()
    },
    fetchRefund(
      fetchRefunds: any,
      selectedDateInfo: any,
      fetchBulkRefunds: any,
      tab: any,
      fetchTxnColumns: any,
      abortController?: any,
    ) {
      const pageNumber = self.transactions.pagination.number === 0 ? 1 : self.transactions.pagination.number
      const size = self.transactions.pagination.size < 10 ? 10 : self.transactions.pagination.size
      const mid = self.user.selectedMid
      const toDate = selectedDateInfo.toDate
      const fromDate = selectedDateInfo.fromDate
      const isFieldsNotEmpty = self.transactions.isFieldsNotEmpty
      const fetchDetailsObj = () => {
        !isFieldsNotEmpty && mid && fetchTxnColumns()
        isFieldsNotEmpty &&
          mid &&
          fetchRefunds(
            pageNumber,
            size,
            `${sortBy.sortField},${sortBy.sortByDesc}`,
            fromDateTime(fromDate),
            toDateTime(toDate ?? new Date(Date.now())),
            mid,
            undefined,
            'refund',
            undefined,
            undefined,
            false,
            abortController,
          )
      }

      return tab === 'Refund History' ? fetchDetailsObj() : mid && fetchBulkRefunds(mid, '', pageNumber - 1, 10)
    },

    fetchSettlementsBasedOnPath(fetchSettlements: any, abortController?: any) {
      let pathName = Routes.SETTLEMENTS
      const pageNumber = self.settlements.pagination.number === 0 ? 1 : self.settlements.pagination.number
      const size = self.settlements.pagination.size < 10 ? 10 : self.settlements.pagination.size
      const mid = self.user.selectedMid
      const toDate = self.settlements.selectedDateInfo.toDate
      const fromDate = self.settlements.selectedDateInfo.fromDate
      const fetchDetailsObj = {
        [Routes.SETTLEMENTS]: () => {
          toDate &&
            mid &&
            fetchSettlements(
              mid,
              fromDateTime(fromDate),
              toDateTime(toDate ?? new Date(Date.now())),
              pageNumber,
              size,
              false,
              false,
              abortController,
            )
        },
      }
      return fetchDetailsObj[pathName]()
    },
    fetchPayLinksOnDateChange(fetchPayLinks: any) {
      const pageNumber = self.links.pagination.number
      const size = self.links.pagination.size < 10 ? 10 : self.links.pagination.size
      const mid = self.user.selectedMid
      const toDate = self.links.selectedDateInfo.toDate
      const fromDate = self.links.selectedDateInfo.fromDate
      const data = fetchPayLinks(
        mid,
        false,
        fromClDateTime(fromDate),
        toCLDateTime(
          toDate ?? new Date(Date.now()),
          self.links.selectedDateInfo.dateLabel === 'Custom Range' ||
            self.links.selectedDateInfo.dateLabel === actionMenuStrings.yesterday,
        ),
        pageNumber,
        size,
      )
      return data
    },
    fetchBulkRefundOnDateChange(fetchBulkSummaryList: any, fileNameQuery?: string) {
      const pageNumber = self.bulkRefund.pagination.number
      const size = self.bulkRefund.pagination.size < 10 ? 10 : self.bulkRefund.pagination.size
      const mid = self.user.selectedMid
      const data = fetchBulkSummaryList(mid, fileNameQuery, pageNumber, size)
      return data
    },
    onTransactionRetry(
      fetchTxn: any,
      fetchTxnColumns: any,
      selectedDateInfo?: any,
      ismobile = false,
      abortController?: any,
    ) {
      const pagination = isOsMobile() ? self.transactions.paginationRecived : self.transactions.pagination
      let pathName = Routes.TRANSACTIONS
      const mid = self.user.selectedMid
      const isFieldsNotEmpty = self.transactions.isFieldsNotEmpty
      const pageNumber = pagination.number === 0 ? 1 : pagination.number
      const size = pagination.size < 10 ? 10 : pagination.size
      const toDate = new Date(selectedDateInfo?.toDate?.getTime() ?? self.transactions?.selectedDateInfo?.toDate)
      const fromDate = new Date(selectedDateInfo?.fromDate?.getTime() ?? self.transactions?.selectedDateInfo?.fromDate)
      const retryObject = {
        [Routes.TRANSACTIONS]: () => {
          isFieldsNotEmpty
            ? fetchTxn(
                pageNumber,
                size,
                `${sortBy.sortField},${sortBy.sortByDesc}`,
                fromDateTime(fromDate),
                toDateTime(toDate ?? new Date(Date.now())),
                mid,
                ...Array(12).fill(undefined),
                selectedDateInfo,
                undefined,
                ismobile,
                abortController,
              )
            : fetchTxnColumns()
        },
      }
      retryObject[pathName]()
    },

    onRefundRetry(fetchRefunds: any, ismobile = false, selectedDateInfo?: any, abortController?: any) {
      const pagination = isOsMobile() ? self.transactions.paginationRefunded : self.transactions.pagination
      const selectedDate = selectedDateInfo ?? self.transactions.selectedDateInfo
      let pathName = Routes.REFUNDS
      const mid = self.user.selectedMid
      const pageNumber = pagination.number === 0 ? 1 : pagination.number
      const size = pagination.size < 10 ? 10 : pagination.size
      const toDate = selectedDate.toDate
      const fromDate = selectedDate.fromDate
      const retryObject = {
        [Routes.REFUNDS]: () => {
          fetchRefunds(
            pageNumber,
            size,
            `${sortBy.sortField},${sortBy.sortByDesc}`,
            fromDateTime(fromDate),
            toDateTime(toDate ?? new Date(Date.now())),
            mid,
            undefined,
            undefined,
            undefined,
            undefined,
            ismobile,
            abortController,
          )
        },
      }
      retryObject[pathName]()
    },

    onSettlementRetry(fetchSettlements: any) {
      let pathName = Routes.SETTLEMENTS
      const mid = self.user.selectedMid
      const pageNumber = self.settlements.pagination.number === 0 ? 1 : self.settlements.pagination.number
      const size = self.settlements.pagination.size < 10 ? 10 : self.settlements.pagination.size
      const toDate = self.settings.metaData.selectedDate.toDate
      const fromDate = self.settings.metaData.selectedDate.fromDate
      const retryObject = {
        [Routes.SETTLEMENTS]: () => {
          fetchSettlements(mid, fromDateTime(fromDate), toDateTime(toDate ?? new Date(Date.now())), pageNumber, size)
        },
      }
      retryObject[pathName]()
    },

    loadMoreTxns(
      fetchTxn: any,
      fetchTxnColumns: any,
      dateObject = self.transactions.receivedSelectedDateInfo,
      ismobile = false,
      abortController,
    ) {
      const mid = self.user.selectedMid
      const isFieldsNotEmpty = self.transactions.isFieldsNotEmpty
      const pageNumber = self.transactions.paginationRecived.number
      const size = self.transactions.paginationRecived.size < 10 ? 10 : self.transactions.paginationRecived.size
      const toDate = dateObject.toDate
      const fromDate = dateObject.fromDate
      let pathName = Routes.TRANSACTIONS
      const retryObject = {
        [Routes.TRANSACTIONS]: () => {
          isFieldsNotEmpty
            ? fetchTxn(
                pageNumber + 1,
                size,
                `${sortBy.sortField},${sortBy.sortByDesc}`,
                fromDateTime(fromDate),
                toDateTime(toDate ?? new Date(Date.now()), dateObject?.dateLabel),
                mid,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                true,
                'Transactions',
                true,
                false,
                dateObject,
                undefined,
                ismobile,
                abortController,
              )
            : fetchTxnColumns()
        },
      }
      retryObject[pathName]()
    },
    loadMoreTxnsSearch(fetchTxn: any, fetchTxnColumns: any) {
      const mid = self.user.selectedMid
      const isFieldsNotEmpty = self.transactions.isFieldsNotEmpty
      const pageNumber = self.transactions.pagination.number === 0 ? 1 : self.transactions.pagination.number
      const size = self.transactions.pagination.size < 10 ? 10 : self.transactions.pagination.size
      const toDate = self.settings.metaData.selectedDate.toDate
      const fromDate = self.settings.metaData.selectedDate.fromDate
      let pathName = Routes.TRANSACTIONS
      const retryObject = {
        [Routes.TRANSACTIONS]: () => {
          isFieldsNotEmpty
            ? fetchTxn(
                pageNumber + 1,
                size,
                `${sortBy.sortField},${sortBy.sortByDesc}`,
                fromDateTime(fromDate),
                toDateTime(toDate ?? new Date(Date.now())),
                mid,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                true,
              )
            : fetchTxnColumns()
        },
      }
      retryObject[pathName]()
    },

    loadMoreSettlement(fetchSettlements: any) {
      let pathName = Routes.SETTLEMENTS
      const mid = self.user.selectedMid
      const pageNumber = self.settlements.pagination.number === 0 ? 1 : self.settlements.pagination.number
      const size = self.settlements.pagination.size < 10 ? 10 : self.settlements.pagination.size
      const toDate = self.settings.metaData.selectedDate.toDate
      const fromDate = self.settings.metaData.selectedDate.fromDate
      const retryObject = {
        [Routes.SETTLEMENTS]: () => {
          fetchSettlements(
            mid,
            fromDateTime(fromDate),
            toDateTime(toDate ?? new Date(Date.now())),
            pageNumber + 1,
            size,
            true,
          )
        },
      }
      retryObject[pathName]()
    },

    loadMoreRefund(fetchRefunds: any, ismobile = false, abortContoller?: any) {
      const mid = self.user.selectedMid
      const pageNumber = self.transactions.paginationRefunded.number
      const size = self.transactions.paginationRefunded.size < 10 ? 10 : self.transactions.paginationRefunded.size
      const toDate = self.transactions.refundedSelectedDateInfo.toDate
      const fromDate = self.transactions.refundedSelectedDateInfo.fromDate
      let pathName = Routes.REFUNDS
      const retryObject = {
        [Routes.REFUNDS]: () => {
          fetchRefunds(
            pageNumber + 1,
            size,
            `${sortBy.sortField},${sortBy.sortByDesc}`,
            fromDateTime(fromDate),
            toDateTime(toDate ?? new Date(Date.now()), true),
            mid,
            true,
            'Transactions',
            undefined,
            undefined,
            ismobile,
            abortContoller,
          )
        },
      }
      retryObject[pathName]()
    },
    fetchTxnsDetailsV3: flow(function* fetchTxnsDetailsV3(path: string, txnId: string, transactionDate: string) {
      const mid = self.user.selectedMid
      if (path === Routes.TRANSACTIONS || path === Routes.REFUNDS) {
        // Need to call on both cases we are chaning request inside getTransactionDetails based on path
        return (
          txnId && mid && (yield self.transactions.getTransactionDetailsV3(mid, txnId ?? '', transactionDate, path))
        )
      }
    }),
    updateDatesInAllScreens: flow(function* updateDatesInAllScreens(dateObj: any, isMobile: boolean) {
      if (isMobile) {
        self.links.updateSelectedDateInfo(dateObj)
        self.settings.metaData.updateDates(dateObj)
        self.transactions.updateSelectedDateInfo(dateObj)
        self.bulkRefund.updateSelectedDateInfo(dateObj)
        const infiniteDateObj = {
          fromDate: getSelectedDate(INFINITE_SCROLL_WINDOW_INDEX),
          toDate: new Date(Date.now()),
          position: {
            top: 0,
            left: 0,
            visible: false,
          },
          dateLabel: actionMenuStrings?.infiniteScrollRange,
        }
        self.transactions.updateReceivedSelectedDateInfo(infiniteDateObj)
        self.transactions.updateRefundedSelectedDateInfo(infiniteDateObj)
        self.settlements.updateSelectedDateInfo(infiniteDateObj)
      } else {
        self.links.updateSelectedDateInfo(dateObj)
        self.campaigns.updateSelectedDateInfo(dateObj)
        self.settings.metaData.updateDates(dateObj)
        self.transactions.updateSelectedDateInfo(dateObj)
        self.settlements.updateSelectedDateInfo(dateObj)
        self.bulkRefund.updateSelectedDateInfo(dateObj)
      }
    }),
    resetDatesInPaymentTabs: flow(function* updateDatesInPaymentTabs() {
      const defaultDateObj = {
        fromDate: getSelectedDate(INFINITE_SCROLL_WINDOW_INDEX),
        toDate: new Date(Date.now()),
        position: {
          top: 0,
          left: 0,
          visible: false,
        },
        dateLabel: actionMenuStrings?.infiniteScrollRange,
      }
      self.transactions.updateReceivedSelectedDateInfo(defaultDateObj)
      self.transactions.updateRefundedSelectedDateInfo(defaultDateObj)
      self.settlements.updateSelectedDateInfo(defaultDateObj)
    }),
  }))
  .views((self) => ({
    moreTxnAvailable(path: string): boolean {
      if (path === Routes.TRANSACTIONS) {
        return self.transactions.paginationRecived.number < self.transactions.paginationRecived.totalPages
      } else if (path === Routes.REFUNDS) {
        return self.transactions.paginationRefunded.number < self.transactions.paginationRefunded.totalPages
      } else if (path === Routes.SEARCH) {
        return self.transactions.pagination.number < self.transactions.pagination.totalPages
      }
      return self.settlements.pagination.number < self.settlements.pagination.totalPages
    },
    isMaxDateRangeSelected(selectedDateInfo = self.transactions.selectedDateInfo): boolean {
      const selectedDateTimeInMillis = new Date(selectedDateInfo?.fromDate)?.getTime()
      const today = new Date(Date.now()).getTime()
      const oneDay = 24 * 60 * 60 * 1000
      const diff = Math.floor((today - selectedDateTimeInMillis) / oneDay)
      return diff > 150
    },
    get getVpa() {
      const merchant = self.user.merchants.find((item: any) => item?.mid === self?.user?.selectedMid)
      return merchant?.vpa ?? ''
    },
    areMerchantsAvailable() {
      const merchant = self.user.merchants
      return merchant?.length > 0
    },
    getTransactionScreenData(path: string, isSmallDevice: boolean) {
      return self.transactions.fetchTransaction(tableConfig(path).tabletype, isSmallDevice)
    },
    getRefundScreenData(path: string, isSmallDevice: boolean) {
      return self.transactions.fetchTransaction(tableConfig(path).tabletype, isSmallDevice)
    },
    getSettlementScreenData() {
      return self.settlements.fetchSettlementData()
    },
    getSelectedStatusFilter(): any[] {
      return self.transactions.filter.paymentLinkStatus
    },
    getContactNumberEnteredFilter(): string {
      return self.transactions.filter.contact
    },
    getEmailEnteredFilter(): string {
      return self.transactions.filter.email
    },
    getPayLinkIdEnteredFilter(): string {
      return self.transactions.filter.paymentLinkId
    },
    getMerchantIdEnteredFilter(): string {
      return self.transactions.filter.merchantLinkId
    },
    getAmountEnteredFilter(): string {
      return self.transactions.filter.amount
    },
    getInvoiceNumberEnteredFilter(): string {
      return self.transactions.filter.invoiceNumber
    },
    getSplitActionMenuData(onSplitClick, onRefundClick, txnItem, path) {
      let retData = []
      const txnAmount = txnItem?.details
        ? Number(txnItem?.details?.amount?.replaceAll(',', ''))
        : Number(txnItem?.transactionAmount?.split(' ')?.[1]?.replaceAll(',', ''))
      const isRefundable = txnAmount > txnItem?.refundAmount
      const status = txnItem?.details
        ? txnItem?.details?.status?.toUpperCase()
        : txnItem?.transactionDisposition?.toUpperCase()
      if (self?.userManagement?.canIssueRefund() && isRefundable && status === 'SUCCESS') {
        retData?.push({
          id: '1',
          value: 'Refund',
          icon: 'REFUND_EVENT',
          color: 'PRIMARY',
          transformAngle: '0deg',
          onPress: onRefundClick,
        })
      }
      if (
        self?.settings?.metaData?.isSplitSettlementsEnabled &&
        self?.userManagement?.canCreateSplitTransactions() &&
        status === 'SUCCESS' &&
        txnItem?.essStatus === 'PAYOUT_SPLIT_STAGED'
      ) {
        retData?.push({
          id: '2',
          value: path === 'refunds' ? 'Create Refund Split' : 'Create Split',
          icon: 'SPLIT',
          color: '#9501B0',
          transformAngle: '90deg',
          onPress: onSplitClick,
        })
      }
      return retData
    },
  }))

const RootStore = Root.create({
  user: userInitialData,
  users: [userInitialData1],
  transactions: transactionsData,
  settlements: SettlementInitialData,
  transactionTrends: TransactionTrendData,
  bulkRefund: bulkRefundInitialData,
  settings: settingsData,
  invoices: InvoicesData,
  response: { message: '' },
  links: LinksInitialData,
  subscriptions: SubscriptionHistoryData,
  insights: InsightsData,
  userManagement: UserManagementData,
  campaigns: CampaignsData,
  tokenizationRequests: TokenizationRequestsData,
  reports: ReportsData,
  agentQrSession: sessionDataInitial,
  agentQrUser: userInitialDataAgent,
  agentQrConfig: configData,
  voiceBoxes: VoiceBoxesData,
  beneficiaryOnboarding: BeneficiaryOnboardingData,
  payoutHistory: PayoutHistoryData,
  payoutLinks: PayoutLinksData,
  billPayments: BillPaymentsData,
  superset: SupersetData,
})

onSnapshot(RootStore, (snapshot) => {
  console.log('%c%s', 'color: green; background: yellow; font-size: 14px;', 'SnapShot:', snapshot)
})

onAction(RootStore, (action) => {
  console.log('%c%s', 'color: white; background: blue; font-size: 14px', 'Action Called: ', action)
})

export type RootType = Instance<typeof Root>
export { Root, RootStore }
